import { Component, Input, Output, EventEmitter, OnInit, ViewChild, SimpleChanges } from '@angular/core';
import { FormArray, FormGroup, AbstractControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { ApiPackage, ApiFlight, ApiTraveler, IApiAcknowledgement, IApiMessage, DateInformation, DateParts, FlightRequirementTypes } from '@bhtp-client-api/base';
import { State, BhtpEligibilityService, IApiFlightRecommendationResponse, IApiFlightEligibilityResponse, ApiMessageType } from '@bhtp-client-api/eligibility';
import {
    FlightBuilderComponent,
    ModalService,
    ModalOptions,
    FlightViewModel,
    FlightValidationStatus,
    PostponedFlightsConfig,
    postponedFlightsValidator,
    conditionalFlightsValidator,
    PostponedFlightsViewModel,
    PostponedFlightsWrapperConfig,
    PostponedFlightsWrapperControlNames
} from '@bhtp-client-web/component';
import { EditSectionComponent } from './edit-section.component';
import { FlightDataContainer } from './view-models/flights-data.model';
import { Constants, FeatureService, Flight } from './../shared';
import { PackageDetails } from '@bhtp-client-api/purchase';

import * as moment from 'moment';

@Component({
    selector: 'flight-edit-component',
    template: require('./flight-edit.component.html')
})
export class FlightEditComponent extends EditSectionComponent<FlightDataContainer> implements OnInit {

    public forms: FormArray;
    @Input() flightData: FlightDataContainer = new FlightDataContainer;
    @Input() departureDate: string;
    @Input() returnDate: string;
    @Input() validateFlightDates: boolean = false;
    @Input() eligibilityToken: string;

    public form: FormGroup;
    public postponedFlightsConfig: PostponedFlightsWrapperConfig;
    public postponedFlightData: PostponedFlightsViewModel;
    public flightsNowToolTip: string;
    public flightsForm: FormArray;
    public messages: any;

    @ViewChild(FlightBuilderComponent) flightBuilder: FlightBuilderComponent;

    private blockedProvidersBaseUrl: string;
    private minDate: string;
    private maxDate: string;

    private departureDates: DateInformation;
    private returnDates: DateInformation;
    private showFlightBuilder: boolean;

    private formInitialized: boolean;

    constructor(private features: FeatureService) {
        super();

        this.blockedProvidersBaseUrl = this.features.getConsumerDomain();
    }

    public get postponedFormValue(): PostponedFlightsViewModel {
        let postponedForm: AbstractControl = this.form.get(PostponedFlightsWrapperControlNames.postponedFlights);

        if (postponedForm) {
            return postponedForm.value;
        }

        return undefined;
    }

    public ngOnInit(): void {
    }

    public get packageFlightsAreConditional(): boolean {

        if (this.flightData && this.flightData.package && this.flightData.package.configuration && this.flightData.package.configuration.flightRequirementType === FlightRequirementTypes.conditional) {
            return true;
        }
        else {
            return false;
        }
    }

    public populateMessaging(): void {
        this.messages = {
            'flightOrPostponeRequired': `Please add flights or check the box confirming that you don’t want to add flights.`
        };

        this.flightsNowToolTip = `If flight information is provided prior to the departure date, flights can be tracked and claims can be automatically created. Insuring more than 4 flights will incur additional premium charges.`;

        this.postponedFlightsConfig = {
            futureFlightsExpectedConfirmMessage: `Great! We'll be reminding you and/or your customer before their trip's departure to add their flights to their policy.`,
            noFutureFlightsExpectedConfirmMessage: `If their itinerary changes, you and/or your customer can always add flights to their policy before their departure date.`,
            flightsLaterRequired: 'Please confirm whether your customer will be flying. If they are flying, we will remind you and/or your customer to add flights to their policy.',
            flightsNowPrompt: `I don't want to add my customer's flights right now.`,
            flightsLaterPrompt: `Will your customer be flying during their trip?`,
            flightsLaterPositive: `Yes`,
            flightsLaterNegative: `No`
        };
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.flightData && (!this.formInitialized || this.flightOptionsChanged(changes))) {
            this.postponedFlightData = {
                noFlights: this.flightData.overrideRequiresFlights,
                flightsLater: this.flightData.futureFlightsExpected
            };

            this.populateMessaging();
            this.showFlightBuilder = !Boolean(this.packageFlightsAreConditional && this.flightData.overrideRequiresFlights);

            if (this.flightOptionsChanged(changes)) {
                this.flightBuilder.setFormInitializedToFalse();
            }

            this.buildForm();
        }
        else if (this.formInitialized) {
            this.setupMinMaxDates();
        }
    }

    public canContinue(isQuickQuote: boolean): Observable<boolean> {
        if (!this.form) {
            return Observable.of(true);
        }
        else if (this.showFlightBuilder) {
            let flightStatus: FlightValidationStatus = FlightViewModel.getFlightsValidationStatus(this.forms, this.flightData.package.configuration.flightRequirementType);

            switch (flightStatus) {
                case FlightValidationStatus.Valid_SkipTripEligibility:
                    return Observable.of(true);
                case FlightValidationStatus.Valid_VerifyTripEligibility:
                    return this.flightBuilder.checkAllFlights();
                default:
                    return Observable.of(false);
            }
        } else {
            let postponedFormControl: AbstractControl = this.form.get(PostponedFlightsWrapperControlNames.postponedFlights);
            if (postponedFormControl) {
                return Observable.of(postponedFormControl.valid);
            } else {
                return Observable.of(false);
            }
        }
    }

    public writeValues(flightData: FlightDataContainer): void {
        if (flightData && flightData.package && flightData.package.configuration) {
            let flightsStatus: FlightValidationStatus = FlightViewModel.getFlightsValidationStatus(this.forms, flightData.package.configuration.flightRequirementType);

            if (flightsStatus === FlightValidationStatus.Valid_SkipTripEligibility ||
                flightsStatus === FlightValidationStatus.Valid_VerifyTripEligibility) {
                flightData.flights = new Array<Flight>();
                this.forms.controls.forEach(fg => {
                    let flight: Flight = fg.value;
                    if (flight) {
                        flightData.flights.push(flight);
                    }
                });

                if (flightData.flights.length > 0) {
                    flightData.overrideRequiresFlights = false;
                }
            }
            else {
                // copy over postponed flight data
                let postponedFlightsData: PostponedFlightsViewModel = this.postponedFormValue;
                if (postponedFlightsData) {
                    flightData.overrideRequiresFlights = postponedFlightsData.noFlights;
                    flightData.futureFlightsExpected = postponedFlightsData.flightsLater;
                }
            }
        }
    }

    public onProductSwap(newRatingId: string): void {
        // Emit Product swap though that does not exist for edit
    }

    public onAcknowledgementsAccepted(acknowledgements: IApiAcknowledgement[]): void {
        const requote: boolean = this.areThereNewOrRemovedAcknowledgements(this.flightData.acknowledgements, acknowledgements);
        this.flightData.acknowledgements = acknowledgements;
        // They have accepted a new acknowledgement, lets be sure to requote
        if (requote) {
            this.onRequoteFlights(this.flightData.flights.length);
        }
    }

    public onRequoteFlights(flightCount: number): void {
        let flightData: FlightDataContainer = new FlightDataContainer();
        flightData.numberOfFlights = flightCount;
        this.valueChanges.emit(flightData);
    }

    public buildForm(): void {
        this.setupMinMaxDates();

        this.forms = new FormArray([]);
        this.flightsForm = new FormArray([]);

        if (!this.flightData.flights || this.flightData.flights.length === 0) {
            this.flightData.flights.push(new Flight());
        }

        this.form = new FormGroup({
            flights: this.flightsForm
        });

        if (this.flightData.package.configuration.flightRequirementType === FlightRequirementTypes.conditional) {
            let previousNoFlightsValue: boolean = this.flightData.overrideRequiresFlights;

            this.form.valueChanges.subscribe((value: any) => {
                if (value.postponedFlights) {
                    let postponed: PostponedFlightsViewModel = value.postponedFlights;
                    // Only perform these tasks if noFlights changed since the last event
                    if (postponed.noFlights !== previousNoFlightsValue) {
                        if (postponed.noFlights) {
                            this.toggleFlightsOff();

                            // If they are opting to add flights late we need to requote with 0 flights
                            this.onRequoteFlights(0);
                        } else {
                            this.toggleFlightsOn();
                            // if they are adding flights we need to requote with flights
                            this.onRequoteFlights(this.flightData.flights.length);
                        }
                    }

                    previousNoFlightsValue = postponed.noFlights;
                }
            });

            this.form.setValidators([conditionalFlightsValidator()]);
        }

        if (this.flightData.package.configuration.flightRequirementType === FlightRequirementTypes.required ||
            this.flightData.package.configuration.flightRequirementType === FlightRequirementTypes.conditional) {
            this.forms.valueChanges.subscribe((value: any) => {
                // requote only if all flight information filled in
                const flightStatus: FlightValidationStatus = FlightViewModel.getFlightsValidationStatus(this.forms, this.flightData.package.configuration.flightRequirementType);
                if (flightStatus === FlightValidationStatus.Valid_VerifyTripEligibility) {
                    this.onRequoteFlights(this.forms.length);
                }
            });
        }

        this.formInitialized = true;
    }

    private areThereNewOrRemovedAcknowledgements(existingAcknowledgements: IApiAcknowledgement[], incomingAcknowledgements: IApiAcknowledgement[] ): boolean {
        const existingAcknowledgementsCount: number = existingAcknowledgements ? existingAcknowledgements.length : 0;
        const incomingAcknowledgementsCount: number = incomingAcknowledgements ? incomingAcknowledgements.length : 0;

        return (existingAcknowledgementsCount !== incomingAcknowledgementsCount);
    }

    private toggleFlightsOn(): void {
        this.showFlightBuilder = true;
    }

    private toggleFlightsOff(): void {
        this.showFlightBuilder = false;
    }

    private setupMinMaxDates(): void {

        if (this.validateFlightDates) {
            // if departure and return dates are present, buffer the max and min dates
            // by a day to both sides
            if (this.departureDate) {
                let departureDate = moment(this.departureDate);
                if (departureDate.isValid()) {
                    departureDate = departureDate.startOf('day');

                    // if departure date is tomorrow, don't subtract a buffer day
                    if (departureDate.isSame(moment().add(1, 'day').startOf('day'))) {
                        this.minDate = departureDate.format(Constants.DATE_PARSE_FORMAT);
                    }
                    else {
                        this.minDate = departureDate.add(-1, 'day').startOf('day').format(Constants.DATE_PARSE_FORMAT);
                    }
                }

                this.departureDates = new DateInformation();
                this.departureDates.localized = new DateParts();
                this.departureDates.localized.fullDateTime = this.departureDate;

                if (this.returnDate) {
                    let returnDate = moment(this.returnDate);
                    if (returnDate.isValid()) {
                        this.maxDate = returnDate.add(1, 'day').startOf('day').format(Constants.DATE_PARSE_FORMAT);
                    }

                    this.returnDates = new DateInformation();
                    this.returnDates.localized = new DateParts();
                    this.returnDates.localized.fullDateTime = this.returnDate;
                }
            }
        }
    }

    private flightOptionsChanged(changes: any): boolean {
        if (changes && changes.flightData && changes.flightData.currentValue && changes.flightData.previousValue) {
            return changes.flightData.currentValue.overrideRequiresFlights !== changes.flightData.previousValue.overrideRequiresFlights;
        } else {
            return false;
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/policy-edit/flight-edit.component.ts